import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { canSee, ROLES } from "@/lib/roles";
import { api } from "@/lib/api";
import {
  SquaresFour, Coins, Receipt, ClipboardText, FileText,
  UsersThree, Truck, ShieldCheck, ChartBar, SignOut, UserGear, Archive, ListMagnifyingGlass, ChatCircleDots, ClipboardText as ClipIcon, HandCoins, CheckSquare, Wallet, CurrencyCircleDollar, CaretRight, Package,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { NoticeBell } from "@/components/NoticeBell";

// Nav definition — items with `children` render as collapsible groups.
// Group `key` is used only for expansion state; it is NOT gated by canSee.
const NAV = [
  { key: "dashboard",       label: "Dashboard",           path: "/",                icon: SquaresFour },
  { key: "budgets",         label: "Requests & Vouchers", path: "/budget-requests", icon: Coins },
  { key: "liquidations",    label: "Liquidations",        path: "/liquidations",    icon: HandCoins },
  { key: "cash_position",   label: "Cash Position",       path: "/cash-position",   icon: Wallet },
  { key: "quotations",      label: "Quotations",          path: "/quotations",      icon: FileText },
  {
    key: "accounts_payable", label: "Accounts Payable", icon: Wallet,
    children: [
      { key: "purchase_orders", label: "Purchase Orders",  path: "/purchase-orders", icon: ClipboardText },
      { key: "billing",         label: "Billing Requests", path: "/billing-requests", icon: Receipt },
    ],
  },
  {
    key: "accounts_receivable", label: "Accounts Receivable", icon: CurrencyCircleDollar,
    children: [
      { key: "receivables",         label: "Receivables",         path: "/receivables",         icon: Receipt },
      { key: "vat_summary",         label: "VAT Summary",         path: "/vat-summary",         icon: Receipt },
      { key: "bank_reconciliation", label: "Bank Reconciliation", path: "/bank-reconciliation", icon: Coins },
      { key: "client_reports",      label: "Client Reports",      path: "/client-reports",      icon: ClipIcon },
    ],
  },
  { key: "clients",         label: "Clients",     path: "/clients",   icon: UsersThree },
  { key: "suppliers",       label: "Suppliers",   path: "/suppliers", icon: Truck },
  { key: "permits",         label: "Permit Repo", path: "/permits",   icon: ShieldCheck },
  {
    key: "inventory_group", label: "Inventory", icon: Package,
    children: [
      { key: "inventory",      label: "Stock",                    path: "/inventory",      icon: Package },
      { key: "accountability", label: "Employee Accountability",  path: "/accountability", icon: UserGear },
    ],
  },
  { key: "reports",         label: "Reports",     path: "/reports",   icon: ChartBar },
  { key: "chat",            label: "Chat",        path: "/chat",      icon: ChatCircleDots },
  { key: "todos",           label: "My To-Do",    path: "/todos",     icon: CheckSquare },
  { key: "trash",           label: "Archived",    path: "/archived",  icon: Archive },
  { key: "users",           label: "Users",       path: "/users",     icon: UserGear },
  { key: "audit_log",       label: "Audit Log",   path: "/audit-log", icon: ListMagnifyingGlass },
];

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState({ total: 0, per_channel: {} });

  // Build the visible tree: keep groups whose children the user can see; keep leaves via canSee.
  const visible = useMemo(() => {
    return NAV.map((item) => {
      if (!item.children) return canSee(user, item.key) ? item : null;
      const kids = item.children.filter((c) => canSee(user, c.key));
      return kids.length ? { ...item, children: kids } : null;
    }).filter(Boolean);
  }, [user]);

  // Groups auto-expand when the current route matches one of their children.
  const [openGroups, setOpenGroups] = useState(() => new Set());
  useEffect(() => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      for (const item of NAV) {
        if (item.children && item.children.some((c) => location.pathname.startsWith(c.path))) {
          next.add(item.key);
        }
      }
      return next;
    });
  }, [location.pathname]);

  function toggleGroup(key) {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  useEffect(() => {
    if (!user) return;
    let stop = false;
    async function pull() {
      try {
        const { data } = await api.get("/chat/unread");
        if (!stop) setUnread(data || { total: 0, per_channel: {} });
      } catch { /* silent */ }
    }
    pull();
    const t = setInterval(pull, 15000);
    // Also listen for chat WS events on this window (Chat.js dispatches them)
    const onEvt = () => pull();
    window.addEventListener("chat:unread-refresh", onEvt);
    return () => { stop = true; clearInterval(t); window.removeEventListener("chat:unread-refresh", onEvt); };
  }, [user]);

  return (
    <div className="h-screen grid grid-cols-[240px_1fr] bg-background overflow-hidden">
      {/* Sidebar — Security Console */}
      <aside className="sec-console sec-grid flex flex-col h-screen overflow-hidden relative">
        <div className="relative px-6 py-5 border-b border-[hsl(var(--console-border))]">
          <div className="flex items-center gap-3">
            <div className="sec-brackets w-9 h-9 flex items-center justify-center font-heading font-black text-[hsl(var(--console-glow))] bg-[hsl(var(--console-elev))] border border-[hsl(var(--console-glow)/0.35)]">
              C
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-heading font-black text-sm leading-none tracking-tight text-[hsl(var(--console-fg))]">CASEDIST INC.</div>
              <div className="flex items-center gap-1.5 mt-1.5">
                <span className="sec-armed-dot" />
                <span className="sec-tag">Ops · Live</span>
              </div>
            </div>
            {["admin", "procurement", "inventory"].includes(user?.role) && <NoticeBell />}
          </div>
        </div>

        <nav className="relative flex-1 p-3 space-y-1 overflow-y-auto">
          {visible.map((item) => {
            const Icon = item.icon;
            if (item.children) {
              const isOpen = openGroups.has(item.key);
              const activeChild = item.children.some((c) => location.pathname.startsWith(c.path));
              return (
                <div key={item.key} data-testid={`nav-group-${item.key}`}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(item.key)}
                    className={`sec-nav-item w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${activeChild ? "active" : ""}`}
                    data-testid={`nav-group-toggle-${item.key}`}
                    data-active={activeChild ? "true" : "false"}
                    aria-expanded={isOpen}
                  >
                    <Icon size={18} weight={activeChild ? "fill" : "regular"} />
                    <span className="flex-1 text-left">{item.label}</span>
                    <CaretRight size={12} className={`transition-transform ${isOpen ? "rotate-90" : ""}`} />
                  </button>
                  {isOpen && (
                    <div className="ml-3 pl-3 mt-1 space-y-1 border-l border-[hsl(var(--console-border))]" data-testid={`nav-group-children-${item.key}`}>
                      {item.children.map((c) => {
                        const CIcon = c.icon;
                        return (
                          <NavLink
                            key={c.key}
                            to={c.path}
                            data-testid={`nav-${c.key}`}
                            className={({ isActive }) =>
                              `sec-nav-item group flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${isActive ? "active" : ""}`
                            }
                          >
                            {({ isActive }) => (
                              <>
                                <CIcon size={16} weight={isActive ? "fill" : "regular"} />
                                <span className="flex-1">{c.label}</span>
                              </>
                            )}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <NavLink
                key={item.key}
                to={item.path}
                end={item.path === "/"}
                data-testid={`nav-${item.key}`}
                className={({ isActive }) =>
                  `sec-nav-item group flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${isActive ? "active" : ""}`
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon size={18} weight={isActive ? "fill" : "regular"} />
                    <span className="flex-1">{item.label}</span>
                    {item.key === "chat" && unread.total > 0 && (
                      <span
                        className="text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded-full bg-[hsl(var(--console-glow))] text-[hsl(var(--console))]"
                        data-testid="nav-chat-unread-badge"
                      >
                        {unread.total > 99 ? "99+" : unread.total}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="relative p-3 border-t border-[hsl(var(--console-border))]">
          <div className="px-3 py-2">
            <div className="flex items-center gap-2 mb-1">
              <span className="sec-armed-dot" />
              <span className="sec-tag">Operator</span>
            </div>
            <div className="text-xs font-medium text-[hsl(var(--console-fg))] truncate" data-testid="current-user-name">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--console-mute))] mt-0.5">
              {ROLES[user?.role] || user?.role}
            </div>
          </div>
          <button
            type="button"
            data-testid="btn-logout"
            onClick={() => { logout(); nav("/login"); }}
            className="w-full mt-2 h-9 rounded-md border border-[hsl(var(--console-border))] bg-transparent text-[hsl(var(--console-fg))] hover:bg-[hsl(var(--console-glow)/0.10)] hover:border-[hsl(var(--console-glow)/0.5)] transition-colors flex items-center justify-center text-sm font-medium"
          >
            <SignOut size={14} className="mr-2" /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="min-w-0 h-screen overflow-y-auto" data-testid="main-scroll-container">
        <div className="max-w-[1400px] mx-auto p-6 lg:p-8">{children}</div>
      </main>
    </div>
  );
}
